import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { useOperationLogs } from "./useOperationLogs";
import { logDedupError } from "@/lib/error-dedup";
import { getIsOnline } from "@/lib/connectivity";
import { openPeriodLocal } from "@/lib/dataGateway";
import { getBrasiliaDate } from "@/lib/date-utils";

export interface OperationalPeriod {
  id: string;
  restaurant_id: string;
  reference_date: string;
  status: "open" | "closed";
  opened_at: string;
  opened_by: string;
  closed_at: string | null;
  closed_by: string | null;
  notes: string | null;
  total_sales: number;
  total_orders: number;
  created_at: string;
  updated_at: string;
}

const FETCH_DEBOUNCE_MS = 2000;
const PERIOD_STORAGE_KEY = "operational_period_state";
const PERIOD_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h

function savePeriodToStorage(period: OperationalPeriod | null) {
  try {
    if (period) {
      localStorage.setItem(PERIOD_STORAGE_KEY, JSON.stringify(period));
    } else {
      localStorage.removeItem(PERIOD_STORAGE_KEY);
    }
  } catch { /* ignore */ }
}

function loadPeriodFromStorage(): OperationalPeriod | null {
  try {
    const raw = localStorage.getItem(PERIOD_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as OperationalPeriod;
    // TTL: discard cache older than 24h to avoid ghost periods
    if (parsed?.opened_at) {
      const age = Date.now() - new Date(parsed.opened_at).getTime();
      if (age > PERIOD_CACHE_TTL_MS) {
        try { localStorage.removeItem(PERIOD_STORAGE_KEY); } catch { /* ignore */ }
        return null;
      }
    }
    return parsed;
  } catch {
    return null;
  }
}

export function useOperationalPeriod() {
  const { activeRestaurant } = useRestaurantContext();
  const { user } = useAuth();
  const [currentPeriod, setCurrentPeriod] = useState<OperationalPeriod | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { logOperation } = useOperationLogs();
  const lastFetchTime = useRef<number>(0);
  const isFetching = useRef(false);

  const fetchCurrentPeriod = useCallback(async () => {
    if (!activeRestaurant?.id) {
      setIsLoading(false);
      return;
    }

    // Debounce: prevent cascading requests
    const now = Date.now();
    if (now - lastFetchTime.current < FETCH_DEBOUNCE_MS) {
      return;
    }

    // Prevent concurrent fetches
    if (isFetching.current) {
      return;
    }

    // Skip if offline — restore from localStorage
    if (!getIsOnline()) {
      const cached = loadPeriodFromStorage();
      if (cached && cached.restaurant_id === activeRestaurant.id) {
        setCurrentPeriod(cached);
        console.log('[OperationalPeriod] Restored from localStorage (offline)');
      }
      setIsLoading(false);
      return;
    }

    lastFetchTime.current = now;
    isFetching.current = true;

    try {
      const { data, error } = await supabase
        .from("operational_periods")
        .select("*")
        .eq("restaurant_id", activeRestaurant.id)
        .eq("status", "open")
        .order("opened_at", { ascending: false })
        .maybeSingle();

      if (error) throw error;
      
      setCurrentPeriod(data as OperationalPeriod | null);
      savePeriodToStorage(data as OperationalPeriod | null);
    } catch (error) {
      logDedupError("OperationalPeriod.fetch", error);
      // Network/timeout fallback: try cache so PDV doesn't block on lie-fi
      const cached = loadPeriodFromStorage();
      if (cached && cached.restaurant_id === activeRestaurant.id) {
        setCurrentPeriod(cached);
        console.log('[OperationalPeriod] Restored from localStorage (fetch failed)');
      }
    } finally {
      setIsLoading(false);
      isFetching.current = false;
    }
  }, [activeRestaurant?.id]);

  const canOpenPeriodForDate = useCallback(async (referenceDate: string): Promise<boolean> => {
    if (!activeRestaurant) return false;

    try {
      const { data, error } = await supabase
        .from("operational_periods")
        .select("id")
        .eq("restaurant_id", activeRestaurant.id)
        .eq("reference_date", referenceDate)
        .maybeSingle();

      if (error) throw error;
      
      // Can open if no period exists for this date
      return !data;
    } catch (error) {
      console.error("Error checking period availability:", error);
      return false;
    }
  }, [activeRestaurant]);

  const openPeriod = useCallback(async (referenceDate?: string): Promise<OperationalPeriod | null> => {
    if (!activeRestaurant || !user) {
      toast.error("Usuário ou restaurante não identificado");
      return null;
    }

    const dateToUse = referenceDate || getBrasiliaDate();
    const online = getIsOnline();

    // Pré-check de data só ONLINE (consulta a nuvem). Offline, o resolve-or-create do servidor
    // local + o mirror de períodos (8a) cuidam do conflito de data.
    if (online) {
      const canOpen = await canOpenPeriodForDate(dateToUse);
      if (!canOpen) {
        toast.error("Período já existe para esta data", {
          description: "Não é possível reabrir um período já utilizado.",
        });
        return null;
      }
    }

    // === LOCAL-FIRST: abre o caixa no servidor local (gateway) → a nuvem recebe via sync. O
    //     período fica disponível na hora p/ o trigger local require_open_period (criar pedido
    //     novo). opened_by na nuvem vira sistema (push); na UI usamos o user.id real. ===
    {
      const local = await openPeriodLocal({ restaurant_id: activeRestaurant.id, reference_date: dateToUse });
      if (local.kind === "ok") {
        const lp = (local.data as { period: Record<string, any> }).period;
        const period: OperationalPeriod = {
          id: lp.id,
          restaurant_id: lp.restaurant_id,
          reference_date: lp.reference_date ?? dateToUse,
          status: lp.status,
          opened_at: lp.opened_at,
          opened_by: user.id,
          closed_at: lp.closed_at ?? null,
          closed_by: null,
          notes: null,
          total_sales: 0,
          total_orders: 0,
          created_at: lp.created_at ?? lp.opened_at,
          updated_at: lp.opened_at,
        };
        logOperation("open", "operational_period", period.id, { reference_date: dateToUse }).catch(() => {});
        setCurrentPeriod(period);
        savePeriodToStorage(period);
        toast.success("Período aberto com sucesso!", {
          description: `Data de referência: ${new Date(dateToUse).toLocaleDateString("pt-BR")}`,
        });
        return period;
      }
      if (local.kind === "businessError") {
        toast.error("Período já existe para esta data");
        return null;
      }
      // local indisponível
      if (!online) {
        toast.error("Sem internet e sem servidor local", {
          description: "Não é possível abrir o caixa agora.",
        });
        return null;
      }
      // online + local indisponível → cai para o cloud-direct abaixo (preserva opened_by=user.id).
    }

    try {
      const { data, error } = await supabase
        .from("operational_periods")
        .insert({
          restaurant_id: activeRestaurant.id,
          reference_date: dateToUse,
          opened_by: user.id,
          status: "open",
        })
        .select()
        .single();

      if (error) throw error;

      await logOperation("open", "operational_period", data.id, {
        reference_date: dateToUse,
      });

      setCurrentPeriod(data as OperationalPeriod);
      savePeriodToStorage(data as OperationalPeriod);
      toast.success("Período aberto com sucesso!", {
        description: `Data de referência: ${new Date(dateToUse).toLocaleDateString("pt-BR")}`,
      });
      return data as OperationalPeriod;
    } catch (error: any) {
      console.error("Error opening period:", error);
      if (error.code === "23505") {
        toast.error("Período já existe para esta data");
      } else {
        toast.error("Erro ao abrir período");
      }
      return null;
    }
  }, [activeRestaurant, user, canOpenPeriodForDate, logOperation]);

  const closePeriod = useCallback(async (notes?: string): Promise<OperationalPeriod | null> => {
    if (!currentPeriod || !user || !activeRestaurant) {
      toast.error("Nenhum período aberto");
      return null;
    }

    try {
      // Check if there are any open cash registers for this period
      const { data: openRegisters } = await supabase
        .from("cash_registers")
        .select("id")
        .eq("period_id", currentPeriod.id)
        .eq("status", "open");

      if (openRegisters && openRegisters.length > 0) {
        toast.error("Feche todos os turnos de caixa antes de fechar o período", {
          description: `Existem ${openRegisters.length} turno(s) aberto(s).`,
        });
        return null;
      }

      // Check for open orders (pending or preparing)
      const { data: openOrders } = await supabase
        .from("orders")
        .select("id, table_number, tab_number, subtotal")
        .eq("restaurant_id", activeRestaurant.id)
        .in("status", ["pending", "preparing"]);

      if (openOrders && openOrders.length > 0) {
        // Check transfer setting
        const { data: restaurant } = await supabase
          .from("restaurants")
          .select("transfer_pending_consumption")
          .eq("id", activeRestaurant.id)
          .single();

        const transferEnabled = (restaurant as any)?.transfer_pending_consumption ?? false;

        if (!transferEnabled) {
          const tableCount = openOrders.filter(o => o.table_number).length;
          const commandCount = openOrders.filter(o => o.tab_number).length;
          
          toast.error("Existem consumos abertos", {
            description: `${tableCount > 0 ? `${tableCount} mesa(s)` : ""}${tableCount > 0 && commandCount > 0 ? " e " : ""}${commandCount > 0 ? `${commandCount} comanda(s)` : ""} precisam ser finalizadas. Ative a transferência automática nas Configurações ou finalize os consumos.`,
          });
          return null;
        }

        // Transfer is enabled - we'll just close the period, orders will remain and can be closed later
        console.log(`[closePeriod] Transferindo ${openOrders.length} consumos abertos para próximo período`);
        toast.info(`${openOrders.length} consumo(s) transferido(s)`, {
          description: "Os consumos abertos permanecerão disponíveis no próximo turno.",
        });
      }

      // Close all active command entries (unused pre-opened commands)
      const { data: activeEntries } = await supabase
        .from("command_entries")
        .select("id, command_id, command_number")
        .eq("restaurant_id", activeRestaurant.id)
        .eq("status", "active");

      if (activeEntries && activeEntries.length > 0) {
        // Close all active entries with consumed_amount = 0
        await supabase
          .from("command_entries")
          .update({
            status: "closed",
            consumed_amount: 0,
            closed_at: new Date().toISOString(),
          })
          .eq("restaurant_id", activeRestaurant.id)
          .eq("status", "active");

        // Release all commands that were pre-opened
        const commandIds = activeEntries
          .map(e => e.command_id)
          .filter(Boolean) as string[];
        
        if (commandIds.length > 0) {
          await supabase
            .from("commands")
            .update({ status: "available", updated_at: new Date().toISOString() })
            .in("id", commandIds);
        }

        console.log(`[closePeriod] Fechadas ${activeEntries.length} entradas de comanda não utilizadas`);
      }

      // Calculate totals from orders in this period
      const { data: periodOrders } = await supabase
        .from("orders")
        .select("total")
        .eq("period_id", currentPeriod.id)
        .neq("status", "cancelled");

      const totalSales = periodOrders?.reduce((sum, order) => sum + Number(order.total), 0) || 0;
      const totalOrders = periodOrders?.length || 0;

      const { data, error } = await supabase
        .from("operational_periods")
        .update({
          status: "closed",
          closed_at: new Date().toISOString(),
          closed_by: user.id,
          notes,
          total_sales: totalSales,
          total_orders: totalOrders,
        })
        .eq("id", currentPeriod.id)
        .select()
        .single();

      if (error) throw error;

      await logOperation("close", "operational_period", currentPeriod.id, {
        reference_date: currentPeriod.reference_date,
        total_sales: totalSales,
        total_orders: totalOrders,
      });

      setCurrentPeriod(null);
      savePeriodToStorage(null);
      toast.success("Período fechado com sucesso!", {
        description: `Vendas: R$ ${totalSales.toFixed(2)} | Pedidos: ${totalOrders}`,
      });
      return data as OperationalPeriod;
    } catch (error) {
      console.error("Error closing period:", error);
      toast.error("Erro ao fechar período");
      return null;
    }
  }, [currentPeriod, user, activeRestaurant, logOperation]);

  const getPeriodsHistory = useCallback(async (limit: number = 10): Promise<OperationalPeriod[]> => {
    if (!activeRestaurant) return [];

    try {
      const { data, error } = await supabase
        .from("operational_periods")
        .select("*")
        .eq("restaurant_id", activeRestaurant.id)
        .order("reference_date", { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data as OperationalPeriod[];
    } catch (error) {
      console.error("Error fetching periods history:", error);
      return [];
    }
  }, [activeRestaurant]);

  useEffect(() => {
    fetchCurrentPeriod();
  }, [fetchCurrentPeriod]);

  return {
    currentPeriod,
    isLoading,
    isPeriodOpen: !!currentPeriod,
    openPeriod,
    closePeriod,
    canOpenPeriodForDate,
    getPeriodsHistory,
    refetch: fetchCurrentPeriod,
  };
}
